import moment from 'moment';
import { EventI } from '@/store/events/state';

const dayMap: Record<string, number> = {
  SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6,
};

export function normalizeRecurrenceRule(rule: string | null | undefined): string {
  if (!rule) return '';

  const compact = String(rule)
    .replace(/\r/g, '')
    .replace(/\n+/g, ';')
    .trim();

  if (!compact) return '';

  const segments = compact
    .split(';')
    .map((segment) => segment.trim())
    .filter(Boolean);

  const normalized: string[] = [];

  segments.forEach((segment) => {
    const upperSegment = segment.toUpperCase();

    if (upperSegment.startsWith('EXDATE') || upperSegment.startsWith('RDATE')) {
      return;
    }

    if (upperSegment.startsWith('RRULE:')) {
      normalized.push(segment.replace(/^RRULE:/i, ''));
      return;
    }

    if (segment.includes('=')) {
      const [key] = segment.split('=');
      if (key && !/^EXDATE$/i.test(key) && !/^RDATE$/i.test(key)) {
        normalized.push(segment);
      }
    }
  });

  return normalized.join(';');
}

function parseRRule(rrule: string) {
  const rule = normalizeRecurrenceRule(rrule);
  const parts = rule.split(';');
  const rules: Record<string, string> = {};
  parts.forEach((part) => {
    const [key, value] = part.split('=');
    if (key && value) {
      rules[key.toUpperCase()] = value;
    }
  });
  return rules;
}

function expandSingleEvent(event: EventI, windowStart: number, windowEnd: number): EventI[] {
  const rules = parseRRule(event.recurrenceRule || '');
  const freq = rules.FREQ;
  if (!freq) return [event];

  const interval = rules.INTERVAL ? parseInt(rules.INTERVAL, 10) : 1;
  const count = rules.COUNT ? parseInt(rules.COUNT, 10) : Infinity;
  const until = rules.UNTIL ? moment(rules.UNTIL).unix() : Infinity;

  const duration = event.endTime - event.startTime;
  const eventStart = moment.unix(event.startTime);

  const instances: EventI[] = [];
  const currentStart = eventStart.clone();
  let occurrences = 0;

  // For monthly byday like -1SU
  const parseByDay = (byday: string) => {
    const match = byday.match(/^(-?\d+)?([A-Z]{2})$/);
    if (!match) return { num: null, day: null };
    return {
      num: match[1] ? parseInt(match[1], 10) : null,
      day: dayMap[match[2]] !== undefined ? dayMap[match[2]] : null,
    };
  };

  while (occurrences < count && currentStart.unix() <= until && currentStart.unix() <= windowEnd) {
    let addInstance = true;

    if (freq === 'WEEKLY' && rules.BYDAY) {
      const days = rules.BYDAY.split(',').map((d) => dayMap[d]);
      if (!days.includes(currentStart.day())) {
        addInstance = false;
      }
    }

    if (freq === 'MONTHLY' && rules.BYMONTHDAY) {
      const monthDays = rules.BYMONTHDAY.split(',').map(Number);
      if (!monthDays.includes(currentStart.date())) {
        addInstance = false;
      }
    }

    if (freq === 'MONTHLY' && rules.BYDAY) {
      const { num, day } = parseByDay(rules.BYDAY);
      if (day !== null) {
        if (num === -1) {
          // Last 'day' of the month
          const lastDayOfMonth = currentStart.clone().endOf('month').day();
          let diff = lastDayOfMonth - day;
          if (diff < 0) diff += 7;
          const targetDate = currentStart.clone().endOf('month').subtract(diff, 'days');
          if (currentStart.date() !== targetDate.date()) {
            addInstance = false;
          }
        } else if (currentStart.day() !== day) {
          // Handle specific nth day if needed, for now exact match
          addInstance = false;
        }
      }
    }

    // Only add if it falls within our window
    const instanceStartUnix = currentStart.unix();
    const instanceEndUnix = instanceStartUnix + duration;

    if (addInstance) {
      if (instanceEndUnix >= windowStart && instanceStartUnix <= windowEnd) {
        instances.push({
          ...event,
          id: `${event.id}_${instanceStartUnix}`,
          startTime: instanceStartUnix,
          endTime: instanceEndUnix,
        });
      }
      occurrences += 1;
    }

    // Advance currentStart
    if (freq === 'DAILY') {
      currentStart.add(interval, 'days');
    } else if (freq === 'WEEKLY') {
      if (rules.BYDAY) {
        currentStart.add(1, 'days');
        // if we crossed a week boundary, add interval - 1 weeks
        if (currentStart.day() === 0) { // Sunday starts the week for moment
          currentStart.add((interval - 1), 'weeks');
        }
      } else {
        currentStart.add(interval, 'weeks');
      }
    } else if (freq === 'MONTHLY') {
      if (rules.BYDAY || rules.BYMONTHDAY) {
        currentStart.add(1, 'days');
        if (currentStart.date() === 1) {
          currentStart.add((interval - 1), 'months');
        }
      } else {
        currentStart.add(interval, 'months');
      }
    } else if (freq === 'YEARLY') {
      currentStart.add(interval, 'years');
    } else {
      break; // safe fallback
    }
  }

  return instances;
}

export function expandEvents(events: EventI[], windowStart: number, windowEnd: number): EventI[] {
  const expanded: EventI[] = [];
  const seenKeys = new Set<string>();

  events.forEach((event) => {
    // If the event is already an instance generated by the backend (has _ in id)
    // or it has no recurrence rule, do not expand it again.
    if (!event.recurrenceRule || String(event.id).includes('_')) {
      // For non-recurring events or pre-expanded instances, use id as key
      const key = String(event.id);
      if (!seenKeys.has(key)) {
        expanded.push(event);
        seenKeys.add(key);
      }
      return;
    }

    try {
      const instances = expandSingleEvent(event, windowStart, windowEnd);
      instances.forEach((instance) => {
        // Use original id (before the _timestamp suffix) + startTime as key
        const originalId = String(instance.id).split('_')[0];
        const key = `${originalId}_${instance.startTime}`;
        if (!seenKeys.has(key)) {
          expanded.push(instance);
          seenKeys.add(key);
        }
      });
    } catch (e) {
      console.error('Failed to expand event', event.id, e);
      expanded.push(event);
    }
  });

  return expanded;
}
